import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import DashboardCard from '../leave-dashboard/components/DashboardCard';
import RowAvatar from '../leave-dashboard/components/RowAvatar';
import { initialsOf } from '../leave-dashboard/components/TodayOnLeaveCard';
import { Legend, LegendItem } from '../leave-dashboard/styles';
import GenericSelect from '../../components/ui/GenericSelect';
import Button from '../../components/ui/Button';
import {
  HIDDEN_TYPE_COLOR, monthOptions, parseMonthKey, daysInMonth, weekBands, barGeometry, todayOffset, entryColor,
} from './teamLeavesModel';
import {
  ScrollArea, MonthNav, NavBtn, BandHeader, BandLabel, TimelineRow, RowLabel, Track, BandLine, TodayLine, Bar, Tag,
  MoreWrap, PENDING_FILL,
} from './styles';

// "Ayşe K." — son kelimenin baş harfi.
function shortName(fullName) {
  const parts = (fullName || '').split(' ').filter(Boolean);
  if (parts.length <= 1) return fullName || '';
  return `${parts.slice(0, -1).join(' ')} ${parts[parts.length - 1][0]}.`;
}

// Ekibin aylık zaman tüneli: satır = kişi, çubuk = izin (onaylı dolu, bekleyen taralı). Ay penceresi sunucudan
// (firstMonth/lastMonth); "Daha Fazla Göster" sonraki 50 kişiyi ekler.
export default function TeamTimelineCard({
  state, monthKey, firstMonth, lastMonth, canPrev, canNext, goPrev, goNext, setMonth, loadMore, typeColors,
}) {
  const { t, i18n } = useTranslation();
  const data = state.data;
  const people = state.people;
  const { year, month } = monthKey ? parseMonthKey(monthKey) : { year: data?.year, month: data?.month };

  const options = useMemo(() => {
    if (!firstMonth || !lastMonth) return [];
    return monthOptions(firstMonth, lastMonth).map((o) => ({
      id: o.key,
      name: new Date(Date.UTC(o.year, o.month - 1, 1))
        .toLocaleDateString(i18n.language, { month: 'long', year: 'numeric', timeZone: 'UTC' }),
    }));
  }, [firstMonth, lastMonth, i18n.language]);

  const fmtRange = (entry) => {
    const fmt = (iso) => {
      const [y, m, d] = iso.split('-').map(Number);
      return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short', timeZone: 'UTC' });
    };
    if (entry.isHourly) return `${fmt(entry.startDate)} ${entry.startTime}–${entry.endTime}`;
    return entry.startDate === entry.endDate ? fmt(entry.startDate) : `${fmt(entry.startDate)} – ${fmt(entry.endDate)}`;
  };

  const model = useMemo(() => {
    if (!year || !month) return null;
    const total = daysInMonth(year, month);
    const bands = weekBands(year, month);
    const lines = bands.slice(1).map((b) => ((b.startDay - 1) / total) * 100);
    const legendTypes = new Map();
    let hasHidden = false;
    let hasWaiting = false;
    people.forEach((person) => {
      person.leaves.forEach((entry) => {
        if (entry.status === 'waiting') hasWaiting = true;
        if (person.canSeeType && entry.leaveVariationId) {
          legendTypes.set(entry.leaveVariationId, {
            name: entry.leaveVariationName,
            color: entryColor(entry, true, typeColors),
          });
        } else if (!person.canSeeType && entry.status === 'approved') {
          hasHidden = true;
        }
      });
    });
    return {
      bands,
      lines,
      today: todayOffset(data?.today, year, month),
      legend: [...legendTypes.values()],
      hasHidden,
      hasWaiting,
    };
  }, [year, month, people, data?.today, typeColors]);

  const headerExtra = (
    <MonthNav data-export-exclude>
      <NavBtn type="button" onClick={goPrev} disabled={!canPrev} aria-label={t('myTeamLeaves.timeline.prevMonth')} title={t('myTeamLeaves.timeline.prevMonth')} data-testid="team-prev-month">
        <ChevronLeft size={15} />
      </NavBtn>
      <GenericSelect
        options={options}
        value={monthKey}
        onChange={(value) => value && setMonth(value)}
        size="small"
        clearable={false}
      />
      <NavBtn type="button" onClick={goNext} disabled={!canNext} aria-label={t('myTeamLeaves.timeline.nextMonth')} title={t('myTeamLeaves.timeline.nextMonth')} data-testid="team-next-month">
        <ChevronRight size={15} />
      </NavBtn>
    </MonthNav>
  );

  return (
    <DashboardCard
      cardKey="ekibimin-izinleri-zaman-tuneli"
      title={t('myTeamLeaves.timeline.title')}
      span={8}
      spanMd={6}
      loading={state.loading}
      empty={!state.loading && people.length === 0}
      emptyText={state.error ? t('myTeamLeaves.loadError') : t('myTeamLeaves.timeline.empty')}
      headerExtra={headerExtra}
    >
      {model && (
        <ScrollArea>
          <BandHeader>
            {model.bands.map((b) => (
              <BandLabel key={b.label} $days={b.endDay - b.startDay + 1}>{b.label}</BandLabel>
            ))}
          </BandHeader>
          {people.map((person) => (
            <TimelineRow key={person.userId} data-testid={`team-row-${person.userId}`}>
              <RowLabel title={person.fullName}>
                <RowAvatar src={person.profilePicture} name={person.fullName} initials={initialsOf(person.fullName)} size={22} />
                <span className="name">{shortName(person.fullName)}</span>
                {person.relation === 'self' && <Tag $self>{t('myTeamLeaves.tag.self')}</Tag>}
                {person.relation === 'manager' && <Tag>{t('myTeamLeaves.tag.manager')}</Tag>}
              </RowLabel>
              <Track $self={person.relation === 'self'}>
                {model.lines.map((left) => <BandLine key={left} $left={left} />)}
                {model.today !== null && <TodayLine $left={model.today} />}
                {person.leaves.map((entry) => {
                  const geometry = barGeometry(entry, year, month);
                  if (!geometry) return null;
                  const pending = entry.status === 'waiting';
                  const typeName = person.canSeeType ? entry.leaveVariationName : null;
                  const statusText = pending ? t('myTeamLeaves.status.waiting') : t('myTeamLeaves.status.approved');
                  const label = pending ? statusText : (typeName || statusText);
                  return (
                    <Bar
                      key={entry.leaveId}
                      $left={geometry.left}
                      $width={geometry.width}
                      $pending={pending}
                      $color={entryColor(entry, person.canSeeType, typeColors)}
                      title={[person.fullName, statusText, fmtRange(entry), typeName].filter(Boolean).join(' — ')}
                      data-testid={`team-bar-${entry.leaveId}`}
                    >
                      {geometry.width > 12 ? label : ''}
                    </Bar>
                  );
                })}
              </Track>
            </TimelineRow>
          ))}
          <Legend>
            {model.legend.map((entry) => (
              <LegendItem key={entry.name} $color={entry.color}>{entry.name}</LegendItem>
            ))}
            {model.hasHidden && <LegendItem $color={HIDDEN_TYPE_COLOR}>{t('myTeamLeaves.legend.hiddenType')}</LegendItem>}
            {model.hasWaiting && <LegendItem $color={PENDING_FILL}>{t('myTeamLeaves.legend.waiting')}</LegendItem>}
            {model.today !== null && <LegendItem $color="#EF4444">{t('myTeamLeaves.legend.today')}</LegendItem>}
          </Legend>
          {data && people.length < data.totalPeople && (
            <MoreWrap data-export-exclude>
              <Button variant="outline" onClick={loadMore} loading={state.loadingMore} data-testid="team-load-more">
                {t('myTeamLeaves.timeline.loadMore')}
              </Button>
            </MoreWrap>
          )}
        </ScrollArea>
      )}
    </DashboardCard>
  );
}
